/**
 * The QQI CAS component a module was created from, kept to hand while it is
 * built. Collapsed: it is reference, not a task.
 *
 * @module components/studio/CasSourcePanel
 */

import React from "react";

import {
  Accordion,
  AccordionItem,
  type AsyncState,
  Badge,
  CollectionView,
  type Column,
  ErrorAlert,
  ExternalLinkButton,
  KeyValue,
  MutedText,
  useAsync,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { MIMLO, Module } from "@/types";
import type { CasAssessmentTechnique, CasComponent } from "@/types/cas";
import {
  compareAssessmentTechniques,
  type TechniqueComparison,
} from "@/utils/cas-assessment-comparison";
import { indexComponents, loadCasData } from "@/utils/cas-data";

/** The CAS code a module was created from, or undefined for any other module. */
export function casComponentCodeOf(module: Module | null | undefined): string | undefined {
  if (module?.casSource !== true) {
    return undefined;
  }
  return module.casComponentCode ?? module.code;
}

/**
 * Reads the component behind `code` from the CAS catalogue. Ready with
 * `undefined` when the catalogue no longer lists it.
 */
export function useCasComponent(code: string | undefined): AsyncState<CasComponent | undefined> {
  return useAsync(
    () => loadCasData().then((data) => indexComponents(data).get(code ?? "")),
    [code],
    { enabled: Boolean(code) },
  );
}

function SourceDetail({
  component,
  module,
}: {
  component: CasComponent;
  module: Module;
}): React.ReactElement {
  const credits = Number(component.credit_value);
  const creditsDiffer = Number.isFinite(credits) && credits !== module.credits;
  return (
    <div className="space-y-4">
      <KeyValue
        entries={[
          { label: "Component", value: `${component.code} ${component.title}` },
          { label: "Award class", value: component.award_class },
          { label: "NFQ level", value: component.nfq_level },
          {
            label: "Credits",
            value: creditsDiffer
              ? `${component.credit_value} (this module carries ${module.credits})`
              : component.credit_value,
          },
          { label: "Field of learning", value: component.field_of_learning },
        ]}
      />
      <div className="flex flex-wrap gap-2">
        {component.specification_pdf_url ? (
          <ExternalLinkButton
            href={component.specification_pdf_url}
            size="sm"
            icon="file-pdf"
            data-testid="module-cas-source-specification"
          >
            Award specification
          </ExternalLinkButton>
        ) : null}
        {component.source_url ? (
          <ExternalLinkButton href={component.source_url} size="sm" icon="globe">
            QQI record
          </ExternalLinkButton>
        ) : null}
      </div>
    </div>
  );
}

function SourceOutcomes({ mimlos }: { mimlos: readonly MIMLO[] }): React.ReactElement | null {
  if (!mimlos.length) {
    return null;
  }
  return (
    <div className="space-y-2">
      <MutedText>Learning outcomes as imported from the component:</MutedText>
      <ol className="list-decimal space-y-1 pl-6 text-md" data-testid="module-cas-source-outcomes">
        {mimlos.map((mimlo) => (
          <li key={mimlo.id}>{mimlo.text}</li>
        ))}
      </ol>
    </div>
  );
}

function percent(value: number | null, absent: string): string {
  return value === null ? absent : `${value}%`;
}

const TECHNIQUE_COLUMNS: Column<TechniqueComparison>[] = [
  { key: "technique", header: "Technique", render: (row) => row.technique },
  {
    key: "specified",
    header: "Specification",
    render: (row) => percent(row.specified, "Not listed"),
  },
  { key: "authored", header: "This module", render: (row) => percent(row.authored, "None") },
  {
    key: "status",
    header: "Comparison",
    render: (row) =>
      row.specified === row.authored ? (
        <Badge tone="success" size="sm">
          Matches
        </Badge>
      ) : (
        <Badge tone="warning" size="sm">
          {row.specified === null ? "Not in the specification" : "Differs"}
        </Badge>
      ),
  },
];

function SourceAssessment({
  techniques,
  module,
}: {
  techniques: readonly CasAssessmentTechnique[] | undefined;
  module: Module;
}): React.ReactElement {
  if (!techniques?.length) {
    return (
      <MutedText>
        Assessment techniques are set out in the award specification; the catalogue does not include
        them.
      </MutedText>
    );
  }
  return (
    <CollectionView
      storageKey="module-cas-source-assessment"
      caption="Assessment against the specification"
      columns={TECHNIQUE_COLUMNS}
      rows={compareAssessmentTechniques(techniques, module.assessments ?? [])}
      rowKey={(row) => row.technique}
      density="sm"
      emptyState={null}
      data-testid="module-cas-source-assessment"
    />
  );
}

export interface CasSourcePanelProps {
  module: Module;
  code: string;
  source: AsyncState<CasComponent | undefined>;
}

export function CasSourcePanel({ module, code, source }: CasSourcePanelProps): React.ReactElement {
  const component = source.status === "ready" ? source.data : undefined;
  return (
    <Accordion id="module-cas-source-accordion" data-testid="module-cas-source">
      <AccordionItem
        eventKey="module-cas-source"
        headingLevel={3}
        title="Source requirements"
        subtitle={component ? `${code} ${component.title}` : code}
        headerActions={
          <Badge tone="primary" size="sm">
            QQI source
          </Badge>
        }
        data-testid="module-cas-source-item"
      >
        <div className="space-y-4">
          {source.status === "loading" ? (
            <ListBones rows={1} label="Reading the CAS component…" />
          ) : null}
          {source.status === "error" ? (
            <ErrorAlert>The CAS catalogue could not be loaded. {source.error.message}</ErrorAlert>
          ) : null}
          {source.status === "ready" && !component ? (
            <MutedText>
              {code} is not in the CAS catalogue available here. The outcomes below are as imported.
            </MutedText>
          ) : null}
          {component ? <SourceDetail component={component} module={module} /> : null}
          <SourceOutcomes mimlos={module.casOriginalMimlos ?? []} />
          <SourceAssessment techniques={component?.assessment_techniques} module={module} />
        </div>
      </AccordionItem>
    </Accordion>
  );
}
